/*  ========================================================================
    # Component - TestimonialsLarge
    ========================================================================  */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'motion/react';
import useTranslation from '../../hooks/useTranslation';
import useSVGSprite from '../../hooks/useSVGSprite';

const TestimonialsLarge = ({ testimonials }) => {
  const __ = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const sprite = useSVGSprite();
  const isSingle = testimonials.length === 1;

  // Icons
  const iconChevronLeft = `${sprite}#chevron-left`;
  const iconChevronRight = `${sprite}#chevron-right`;

  const canNavigateLeft = () => {
    return activeIndex > 0;
  };

  const canNavigateRight = () => {
    return activeIndex < testimonials.length - 1;
  };

  const handleNavigateLeft = () => {
    if (canNavigateLeft()) {
      setActiveIndex(activeIndex - 1);
    }
  };

  const handleNavigateRight = () => {
    if (canNavigateRight()) {
      setActiveIndex(activeIndex + 1);
    }
  };

  const getSlideAnimation = index => {
    if (index === activeIndex) {
      return { opacity: 1, visibility: 'visible', x: 0 };
    } else if (index < activeIndex) {
      return { opacity: 0, visibility: 'hidden', x: '-100px' };
    } else {
      return { opacity: 0, visibility: 'hidden', x: '100px' };
    }
  };

  return (
    <div className='s-testimonials__slider-wrapper'>
      <div className='s-testimonials__media'>
        {testimonials.map((testimonial, index) => (
          <motion.div
            initial={{ opacity: 0 }}
            animate={index === activeIndex ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.5 }}
            className='s-testimonials__media-item'
            key={`${index}-media`}
          >
            <figure>
              <img src={testimonial.image.sizes.large} alt={testimonial.image.alt} />
            </figure>
          </motion.div>
        ))}
      </div>
      <div className='s-testimonials__slider'>
        {testimonials.map((testimonial, index) => (
          <motion.div
            key={index}
            className='s-testimonials__slide'
            initial={{ opacity: 0 }}
            animate={getSlideAnimation(index)}
            transition={{ duration: 0.5, type: 'spring' }}
            draggable={true}
            drag='x'
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={(event, info) => {
              if (info.offset.x < -100) {
                handleNavigateRight();
              } else if (info.offset.x > 100) {
                handleNavigateLeft();
              }
            }}
          >
            <div className='s-testimonials__item'>
              {testimonial.company_logo && (
                <div className='s-testimonials__item-logo'>
                  <figure>
                    <img src={testimonial.company_logo.sizes.medium_large} alt={testimonial.company_logo.alt} />
                  </figure>
                </div>
              )}
              <div className='s-testimonials__item-quote'>
                <blockquote>
                  <p>{testimonial.quote}</p>
                </blockquote>
              </div>

              <div className='s-testimonials__item-author'>
                <p className='s-testimonials__item-author-name h5'>{testimonial.name}</p>
                {testimonial.position && (
                  <p className='s-testimonials__item-author-position u-text-mid'>{testimonial.position}</p>
                )}
              </div>
            </div>
          </motion.div>
        ))}

      </div>
      {isSingle ? null : (
        <div className='s-testimonials__controls'>
          <span className='s-testimonials__progress' style={{ '--active-item': activeIndex + 1, '--total-items': testimonials.length }}></span>
          <button
            className='s-testimonials__control-prev c-button c-button--icon'
            aria-label={__('testimonials-navigate-left')}
            onClick={() => handleNavigateLeft()}
            disabled={!canNavigateLeft()}
          >
            <i className='u-icon u-icon--chevron-left' aria-hidden></i>
          </button>
          <button
            className='s-testimonials__control-next c-button c-button--icon'
            aria-label={__('testimonials-navigate-right')}
            onClick={() => handleNavigateRight()}
            disabled={!canNavigateRight()}
          >
            <i className='u-icon u-icon--chevron-right' aria-hidden></i>
          </button>
        </div>
      )}
    </div>
  );
};

TestimonialsLarge.propTypes = {
  testimonials: PropTypes.array
};

export default TestimonialsLarge;
